<script lang="ts" setup>
  import { PlanType, SnpType } from '~/generated/api-clients-generated'
  import type { QuotedPlanVm } from '~/models/quoting/QuotedPlan'
  import QuoteDoctorNetworkSidebar from '../../QuoteDoctorNetworkSidebar.vue'
  import { PlanBenefitCostType } from '~/generated/api-clients-generated'

  const props = defineProps<{
    id: string
    quote: QuotedPlanVm
    stacked?: Boolean
  }>()

  const { getId } = Id.use(props.id)
  const { show } = DialogController.use()

  const monthlyDrugCostAverage = computed(() => {
    const monthlyCosts = props.quote?.drugCoverage?.pharmacyCosts?.[0]?.monthlyCosts ?? []

    if (monthlyCosts.length === 0) return 0

    const totalCosts = _sumBy(monthlyCosts, (month) =>
      _sumBy(month.details, (detail) => detail.memberCost ?? 0)
    )

    return totalCosts / monthlyCosts.length
  })

  const inNetworkDoctorClasses = computed(() => {
    switch (true) {
      case props.quote.providerCoverage.inNetworkCount === props.quote.providerCoverage.count:
        return 'text-success'
      case props.quote.providerCoverage.inNetworkCount === 0:
        return 'text-danger'
      default:
        return 'text-primary-900'
    }
  })

  const verticalOrMobileStyles = computed(() => {
    return {
      container: props.stacked ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-4',
      score: props.stacked
        ? 'order-1 mb-4 before:left-1/2 before:-translate-x-1/2'
        : 'before:left-1/2 before:-translate-x-1/2 order-1 mb-3 md:mb-0 before:md:-right-2.5 md:before:left-auto md:before:top-1/2 md:before:-translate-y-1/2 md:before:translate-x-0',
      costs: [
        props.stacked ? 'order-3' : 'order-2 md:order-3 md:col-span-2',
        formatUsd(monthlyDrugCostAverage.value).length > 7
          ? 'grid-cols-1 sm:grid-cols-2'
          : 'grid-cols-2'
      ],
      premium: props.stacked ? 'order-2' : 'order-3 md:order-4'
    }
  })

  const formatCopayAmount = (copay: any) => {
    const amount = copay?.amount
    return copay?.amountType === PlanBenefitCostType.Coinsurance
      ? formatPct(amount)
      : formatUsd(amount)
  }

  const pcpCopayAmount = computed(() =>
    formatCopayAmount(props.quote?.details?.providerBenefits?.doctorCopay)
  )

  const specialistCopayAmount = computed(() =>
    formatCopayAmount(props.quote?.details?.providerBenefits?.specialistCopay)
  )
</script>

<template>
  <section class="grid place-items-center gap-2.5" :class="verticalOrMobileStyles.container">
    <HpAnchor
      v-if="quote.type === PlanType.MAPD"
      class="score cost-item group relative bg-sky-100"
      :class="verticalOrMobileStyles.score"
      @click.prevent="show(getId(`score-calc-sidebar`))"
    >
      <p
        class="flex flex-wrap items-center justify-center gap-1 pb-1 text-lg font-semibold leading-tight underline"
      >
        <HpIcon name="healthpilot-icon-simple" class="size-5" />
        Your Plan Fit Score
      </p>
      <QuotePlanScoreV2
        :score="quote.score"
        band-thickness="thick"
        size="large"
        @click="show(getId(`score-calc-sidebar`))"
        class="transition-transform group-hover:scale-[1.02]"
      />
      <QuoteCardScoreSidebar :id="getId(`score-calc-sidebar`)" :quote="quote" />
    </HpAnchor>

    <div class="grid w-full max-w-md grid-rows-2 gap-2.5" :class="verticalOrMobileStyles.costs">
      <p class="cost-item bg-sky-100">
        <span class="text-4xl leading-none tracking-tight">
          {{ pcpCopayAmount }}
        </span>
        PCP Copay
      </p>

      <p class="cost-item bg-sky-100">
        <span class="text-4xl leading-none tracking-tight">
          {{ specialistCopayAmount }}
        </span>
        Specialist Copay
      </p>

      <HpAnchor
        v-if="quote.drugCoverage.count > 0"
        class="cost-item group bg-sky-100"
        @click.prevent="show(getId('drug-monthly-card-breakdown-sidebar'))"
      >
        <span
          class="text-4xl leading-none tracking-tight transition-transform group-hover:scale-[1.02]"
        >
          {{ formatUsd(monthlyDrugCostAverage) }}
        </span>
        <span class="text-balance underline underline-offset-2">Monthly Est Drug Cost</span>
      </HpAnchor>

      <HpAnchor
        v-else
        class="cost-item group bg-sky-100"
        @click.prevent="show('update-insurance-sidebar')"
      >
        <span
          class="text-balance text-lg font-semibold leading-tight text-primary underline underline-offset-2"
          >Add Your Drugs to See Costs</span
        >
      </HpAnchor>

      <HpAnchor
        v-if="quote.providerCoverage.count > 0"
        @click.prevent="show(getId('doctors-sidebar'))"
        class="cost-item group bg-sky-100"
      >
        <span
          class="text-4xl leading-none tracking-tight transition-transform group-hover:scale-[1.02]"
          :class="inNetworkDoctorClasses"
        >
          {{ quote.providerCoverage.inNetworkCount }} of {{ quote.providerCoverage.count }}
        </span>
        <span class="underline underline-offset-2">Doctors In-Network</span>
      </HpAnchor>

      <HpAnchor
        v-else
        @click.prevent="show('update-insurance-sidebar')"
        class="cost-item group bg-sky-100"
      >
        <span
          class="text-balance text-lg font-semibold leading-tight text-primary underline underline-offset-2"
          >Add Your Doctors to See Coverage</span
        >
      </HpAnchor>
    </div>

    <p class="cost-item max-w-md bg-success-light/75" :class="verticalOrMobileStyles.premium">
      <span class="text-5xl leading-none tracking-tighter">
        {{ formatUsd(quote.recommendation.premium) }}
      </span>
      Monthly Premium
    </p>
  </section>

  <DetailsDrugMonthlyBreakdownSidebarV2
    :id="getId('drug-monthly-card-breakdown-sidebar')"
    :quote="quote"
  />

  <QuoteDoctorNetworkSidebar :id="getId('doctors-sidebar')" :quote="quote" />
</template>

<style scoped>
  .score::before {
    content: '';
    @apply absolute -bottom-2 -z-10 size-10 rotate-45 rounded-md bg-sky-100;
  }

  .cost-item {
    @apply flex h-full w-full flex-col items-center justify-center gap-1 rounded-3xl px-4 py-3 text-center text-sm leading-none text-primary-900 no-underline;
  }
</style>
